import ApartmentIcon from "@mui/icons-material/Apartment";
import LinkIcon from "@mui/icons-material/Link";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography
} from "@mui/material";
import { FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { FreelancerProfileCompanyUrl } from "../../../../../models/FreelancerProfileCompanyUrl";
import { zoullooColors } from "../../../../../theme";
import {
	AddressViewModel,
	FreelancerOnboardingMyProfessionalInformationStepViewModel
} from "../../../../../ViewModels/FreelancerOnboardingMyProfessionalInformationStepViewModel";
import PhoneInput from "../../../../UI/PhoneInput";
import PlacesAutocomplete from "../../../../UI/PlacesAutocomplete";
import UrlTable from "../common/UrlTable";

interface CompanyInfoSectionProps {
	formik: FormikProps<FreelancerOnboardingMyProfessionalInformationStepViewModel>;
	selectedStatus: "portage" | "structure";
	getCurrentStatusData: (status: "portage" | "structure") => any;
	updateStatusData: (status: "portage" | "structure", data: any) => void;
	getDebouncedUpdate: (fieldPath: string) => (value: string) => void;
	convertPlaceResultToAddressViewModel: (
		placeResult: google.maps.places.PlaceResult
	) => AddressViewModel;
	activeCompanyUrlTable: boolean;
	setActiveCompanyUrlTable: (active: boolean) => void;
	isAddingCompanyUrl: boolean;
	addingCompanyUrl: FreelancerProfileCompanyUrl | null;
	setAddingCompanyUrl: React.Dispatch<
		React.SetStateAction<FreelancerProfileCompanyUrl | null>
	>;
	urlTypes: Array<{ id: number; label: string }>;
	handleAddNewCompanyUrl: () => void;
	handleConfirmAddCompanyUrl: () => void;
	handleCancelAddCompanyUrl: () => void;
	removeCompanyUrl: (index: number) => void;
	setIsCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated: React.Dispatch<
		React.SetStateAction<boolean>
	>;
	fieldRef?: React.RefObject<HTMLInputElement>;
}

const CompanyInfoSection: React.FC<CompanyInfoSectionProps> = ({
	formik,
	selectedStatus,
	getCurrentStatusData,
	updateStatusData,
	getDebouncedUpdate,
	convertPlaceResultToAddressViewModel,
	activeCompanyUrlTable,
	setActiveCompanyUrlTable,
	isAddingCompanyUrl,
	addingCompanyUrl,
	setAddingCompanyUrl,
	urlTypes,
	handleAddNewCompanyUrl,
	handleConfirmAddCompanyUrl,
	handleCancelAddCompanyUrl,
	removeCompanyUrl,
	setIsCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated,
	fieldRef
}) => {
	const { t } = useTranslation();

	const currentStatusData = getCurrentStatusData(selectedStatus);
	const isRegistrationInProgress = currentStatusData?.isRegistrationInProgress === true;

	// Detect whether every required administrative + contact field is filled
	// (acknowledgement is intentionally excluded because it stays hidden while
	// the in-progress flag is on, so the talent can't tick it before the flag
	// is cleared).
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	const companyNameTrimmed = currentStatusData?.companyName?.trim() ?? "";
	const emailTrimmed = currentStatusData?.email?.trim() ?? "";
	const allRequiredFieldsFilled =
		!!currentStatusData?.selectedCountry &&
		!!currentStatusData?.selectedAddress &&
		!!currentStatusData?.identificationNumber?.trim() &&
		companyNameTrimmed.length >= 2 &&
		!!currentStatusData?.firstName?.trim() &&
		!!currentStatusData?.lastName?.trim() &&
		!!emailTrimmed &&
		emailRegex.test(emailTrimmed);

	// Disable the "registration in progress" checkbox only when the data is
	// already complete AND the flag is currently off: greys it out to prevent
	// the talent from flipping consistent data into an "in progress" state.
	// When the flag is already on, we keep the checkbox enabled so the talent
	// can uncheck it (covers the legacy "saved in lenient mode with all fields
	// filled" case).
	const isRegistrationInProgressDisabled =
		allRequiredFieldsFilled && !isRegistrationInProgress;

	const handleRegistrationInProgressChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		updateStatusData(selectedStatus, {
			isRegistrationInProgress: event.target.checked
		});
		formik.setFieldTouched(
			`${selectedStatus}Data.isRegistrationInProgress`,
			true,
			false
		);
		formik.setFieldValue(
			`${selectedStatus}Data.isRegistrationInProgress`,
			event.target.checked,
			true
		);
	};

	return (
		<Box key={selectedStatus} sx={{ mt: 4, borderRadius: "8px" }}>
			<Typography variant="body1" sx={{ mb: 1 }}>
				{t(
					selectedStatus === "structure"
						? "freelancerOnboardingMyProfessionalInformation.companyInfo.descriptionStructure"
						: "freelancerOnboardingMyProfessionalInformation.companyInfo.description"
				)}
			</Typography>

			<Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
				{t(
					selectedStatus === "structure"
						? "freelancerOnboardingMyProfessionalInformation.companyInfo.registrationInProgressHelperStructure"
						: "freelancerOnboardingMyProfessionalInformation.companyInfo.registrationInProgressHelperPortage"
				)}
			</Typography>

			<Box sx={{ mb: 3 }}>
				<FormControlLabel
					control={
						<Checkbox
							color="secondary"
							checked={isRegistrationInProgress}
							onChange={handleRegistrationInProgressChange}
							onBlur={() =>
								formik.setFieldTouched(
									`${selectedStatus}Data.isRegistrationInProgress`,
									true
								)
							}
							name={`${selectedStatus}Data.isRegistrationInProgress`}
							disabled={isRegistrationInProgressDisabled}
						/>
					}
					label={t(
						selectedStatus === "structure"
							? "freelancerOnboardingMyProfessionalInformation.companyInfo.registrationInProgressLabelStructure"
							: "freelancerOnboardingMyProfessionalInformation.companyInfo.registrationInProgressLabelPortage"
					)}
				/>
			</Box>

			{/* Administrative Information Section */}
			<Typography variant="h6" sx={{ mb: 2 }}>
				{t(
					"freelancerOnboardingMyProfessionalInformation.companyInfo.administrativeInfo"
				)}
			</Typography>

			<Grid2 container spacing={2} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<PlacesAutocomplete
						name={`${selectedStatus}Data.selectedCountry`}
						value={getCurrentStatusData(selectedStatus)?.selectedCountry || null}
						onChange={place => {
							const currentData = getCurrentStatusData(selectedStatus);
							const previousCountry = currentData?.selectedCountry;

							// Only clear address if country actually changed (not just initial load)
							const shouldClearAddress =
								previousCountry &&
								(!place || place.place_id !== previousCountry.place_id);

							updateStatusData(selectedStatus, {
								selectedCountry: place,
								...(shouldClearAddress && { selectedAddress: null })
							});

							if (shouldClearAddress || !place) {
								formik.setFieldTouched(`${selectedStatus}Data.selectedAddress`, true);
							} else {
								formik.setFieldTouched(`${selectedStatus}Data.selectedAddress`, false);
							}

							setTimeout(() => {
								setIsCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated(
									place?.place_id ===
										getCurrentStatusData(selectedStatus).selectedCountry?.place_id
								);
							}, 200);
						}}
						onBlur={formik.handleBlur}
						types={["country"]}
						componentRestrictions={{ country: [] }}
						fields={["address_components", "formatted_address", "name", "place_id"]}
						minInputLength={2}
						startIcon={
							<PublicOutlinedIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />
						}
						label={t(
							"freelancerOnboardingMyProfessionalInformation.companyInfo.countryLabel",
							{
								type:
									selectedStatus === "portage"
										? t(
												"freelancerOnboardingMyProfessionalInformation.companyInfo.countryLabelPortage"
										  )
										: t(
												"freelancerOnboardingMyProfessionalInformation.companyInfo.countryLabelStructure"
										  )
							}
						)}
						placeholder={t(
							"freelancerOnboardingMyProfessionalInformation.companyInfo.selectCountry"
						)}
						variant="outlined"
						fullWidth
						error={
							(formik.touched[`${selectedStatus}Data`] as any)?.selectedCountry &&
							Boolean(
								(
									formik.errors[
										`${selectedStatus}Data` as keyof typeof formik.errors
									] as any
								)?.selectedCountry
							)
						}
						helperText={
							(formik.touched[`${selectedStatus}Data`] as any)?.selectedCountry &&
							(
								formik.errors[
									`${selectedStatus}Data` as keyof typeof formik.errors
								] as any
							)?.selectedCountry
						}
					/>
				</Grid2>
			</Grid2>

			<Grid2 container spacing={2} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						inputRef={fieldRef}
						key={`identificationNumber-${selectedStatus}`}
						fullWidth
						name={`${selectedStatus}Data.identificationNumber`}
						defaultValue={
							getCurrentStatusData(selectedStatus)?.identificationNumber || ""
						}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
							getDebouncedUpdate("identificationNumber")(event.target.value)
						}
						onBlur={formik.handleBlur}
						label={t(
							"freelancerOnboardingMyProfessionalInformation.companyInfo.identificationNumber"
						)}
						placeholder={t(
							"freelancerOnboardingMyProfessionalInformation.companyInfo.identificationNumberPlaceholder"
						)}
						variant="outlined"
						error={
							(formik.touched[`${selectedStatus}Data`] as any)?.identificationNumber &&
							Boolean(
								(
									formik.errors[
										`${selectedStatus}Data` as keyof typeof formik.errors
									] as any
								)?.identificationNumber
							)
						}
						helperText={
							(formik.touched[`${selectedStatus}Data`] as any)?.identificationNumber &&
							(
								formik.errors[
									`${selectedStatus}Data` as keyof typeof formik.errors
								] as any
							)?.identificationNumber
						}
					/>
				</Grid2>
			</Grid2>

			<Grid2 container spacing={2} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						key={`companyName-${selectedStatus}`}
						fullWidth
						name={`${selectedStatus}Data.companyName`}
						defaultValue={getCurrentStatusData(selectedStatus)?.companyName || ""}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
							getDebouncedUpdate("companyName")(event.target.value)
						}
						onBlur={formik.handleBlur}
						label={t(
							"freelancerOnboardingMyProfessionalInformation.companyInfo.companyName"
						)}
						variant="outlined"
						error={
							(formik.touched[`${selectedStatus}Data`] as any)?.companyName &&
							Boolean(
								(
									formik.errors[
										`${selectedStatus}Data` as keyof typeof formik.errors
									] as any
								)?.companyName
							)
						}
						helperText={
							(formik.touched[`${selectedStatus}Data`] as any)?.companyName &&
							(
								formik.errors[
									`${selectedStatus}Data` as keyof typeof formik.errors
								] as any
							)?.companyName
						}
					/>
				</Grid2>
			</Grid2>

			<Grid2 container spacing={2} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<PlacesAutocomplete
						name={`${selectedStatus}Data.selectedAddress`}
						value={
							getCurrentStatusData(selectedStatus)?.selectedAddress
								? ({
										place_id:
											getCurrentStatusData(selectedStatus).selectedAddress.placeId,
										formatted_address:
											getCurrentStatusData(selectedStatus).selectedAddress.fullAddress
								  } as google.maps.places.PlaceResult)
								: null
						}
						onChange={place => {
							if (!place) {
								updateStatusData(selectedStatus, {
									selectedAddress: null
								});
								setTimeout(() => {
									setIsCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated(
										getCurrentStatusData(selectedStatus).selectedAddress?.placeId === null
									);
								}, 200);

								return;
							}

							const addressViewModel = convertPlaceResultToAddressViewModel(place);

							updateStatusData(selectedStatus, {
								selectedAddress: addressViewModel
							});

							setTimeout(() => {
								setIsCountryOrAddressValueChangedOnLoadBecauseOfBeingTranslated(
									place?.place_id ===
										getCurrentStatusData(selectedStatus).selectedAddress?.placeId
								);
							}, 200);
						}}
						onBlur={formik.handleBlur}
						disabled={!getCurrentStatusData(selectedStatus)?.selectedCountry}
						types={["address"]}
						componentRestrictions={{
							country: (() => {
								const currentData = getCurrentStatusData(selectedStatus);
								const selectedCountry = currentData?.selectedCountry;
								if (!selectedCountry?.address_components) {
									return [];
								}

								const countryComponent = selectedCountry.address_components.find(
									(component: any) => component.types.includes("country")
								);
								return countryComponent?.short_name ? [countryComponent.short_name] : [];
							})()
						}}
						fields={[
							"address_components",
							"formatted_address",
							"name",
							"place_id",
							"geometry"
						]}
						minInputLength={3}
						startIcon={<ApartmentIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />}
						label={t(
							"freelancerOnboardingMyProfessionalInformation.companyInfo.companyAddress"
						)}
						placeholder={t(
							"freelancerOnboardingMyProfessionalInformation.companyInfo.enterAddress"
						)}
						variant="outlined"
						fullWidth
						error={
							(formik.touched[`${selectedStatus}Data`] as any)?.selectedAddress &&
							Boolean(
								(
									formik.errors[
										`${selectedStatus}Data` as keyof typeof formik.errors
									] as any
								)?.selectedAddress
							)
						}
						helperText={
							(formik.touched[`${selectedStatus}Data`] as any)?.selectedAddress &&
							(
								formik.errors[
									`${selectedStatus}Data` as keyof typeof formik.errors
								] as any
							)?.selectedAddress
						}
					/>
				</Grid2>
			</Grid2>

			<Grid2 container spacing={2} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						key={`vatNumber-${selectedStatus}`}
						fullWidth
						name={`${selectedStatus}Data.vatNumber`}
						defaultValue={getCurrentStatusData(selectedStatus)?.vatNumber || ""}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
							getDebouncedUpdate("vatNumber")(event.target.value)
						}
						onBlur={formik.handleBlur}
						label={t(
							"freelancerOnboardingMyProfessionalInformation.companyInfo.vatNumber"
						)}
						variant="outlined"
						error={
							(formik.touched[`${selectedStatus}Data`] as any)?.vatNumber &&
							Boolean(
								(
									formik.errors[
										`${selectedStatus}Data` as keyof typeof formik.errors
									] as any
								)?.vatNumber
							)
						}
						helperText={
							(formik.touched[`${selectedStatus}Data`] as any)?.vatNumber &&
							(
								formik.errors[
									`${selectedStatus}Data` as keyof typeof formik.errors
								] as any
							)?.vatNumber
						}
					/>
				</Grid2>
			</Grid2>

			{/* Contact Section */}
			<Typography variant="h6" sx={{ mb: 2, fontWeight: 500, mt: 4 }}>
				{t("freelancerOnboardingMyProfessionalInformation.contact.title")}
			</Typography>

			<Grid2 container spacing={2} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						key={`firstName-${selectedStatus}`}
						fullWidth
						name={`${selectedStatus}Data.firstName`}
						defaultValue={getCurrentStatusData(selectedStatus)?.firstName || ""}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
							getDebouncedUpdate("firstName")(event.target.value)
						}
						onBlur={formik.handleBlur}
						label={t("freelancerOnboardingMyProfessionalInformation.contact.firstName")}
						variant="outlined"
						error={
							(formik.touched[`${selectedStatus}Data`] as any)?.firstName &&
							Boolean(
								(
									formik.errors[
										`${selectedStatus}Data` as keyof typeof formik.errors
									] as any
								)?.firstName
							)
						}
						helperText={
							(formik.touched[`${selectedStatus}Data`] as any)?.firstName &&
							(
								formik.errors[
									`${selectedStatus}Data` as keyof typeof formik.errors
								] as any
							)?.firstName
						}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						key={`lastName-${selectedStatus}`}
						fullWidth
						name={`${selectedStatus}Data.lastName`}
						defaultValue={getCurrentStatusData(selectedStatus)?.lastName || ""}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
							getDebouncedUpdate("lastName")(event.target.value)
						}
						onBlur={formik.handleBlur}
						label={t("freelancerOnboardingMyProfessionalInformation.contact.lastName")}
						variant="outlined"
						error={
							(formik.touched[`${selectedStatus}Data`] as any)?.lastName &&
							Boolean(
								(
									formik.errors[
										`${selectedStatus}Data` as keyof typeof formik.errors
									] as any
								)?.lastName
							)
						}
						helperText={
							(formik.touched[`${selectedStatus}Data`] as any)?.lastName &&
							(
								formik.errors[
									`${selectedStatus}Data` as keyof typeof formik.errors
								] as any
							)?.lastName
						}
					/>
				</Grid2>
			</Grid2>

			<Grid2 container spacing={2} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						key={`email-${selectedStatus}`}
						fullWidth
						name={`${selectedStatus}Data.email`}
						type="email"
						defaultValue={getCurrentStatusData(selectedStatus)?.email || ""}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
							getDebouncedUpdate("email")(event.target.value)
						}
						onBlur={formik.handleBlur}
						label={t("freelancerOnboardingMyProfessionalInformation.contact.email")}
						variant="outlined"
						error={
							(formik.touched[`${selectedStatus}Data`] as any)?.email &&
							Boolean(
								(
									formik.errors[
										`${selectedStatus}Data` as keyof typeof formik.errors
									] as any
								)?.email
							)
						}
						helperText={
							(formik.touched[`${selectedStatus}Data`] as any)?.email &&
							(
								formik.errors[
									`${selectedStatus}Data` as keyof typeof formik.errors
								] as any
							)?.email
						}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<PhoneInput
						value={getCurrentStatusData(selectedStatus)?.phoneNumber || ""}
						onChange={(phone: string) => {
							updateStatusData(selectedStatus, {
								phoneNumber:
									!(formik.touched[`${selectedStatus}Data`] as any)?.phoneNumber &&
									phone.startsWith("+") &&
									phone.length <= 4
										? ""
										: phone
							});
						}}
						onBlur={formik.handleBlur}
						name={`${selectedStatus}Data.phoneNumber`}
						error={
							(formik.touched[`${selectedStatus}Data`] as any)?.phoneNumber &&
							Boolean(
								(
									formik.errors[
										`${selectedStatus}Data` as keyof typeof formik.errors
									] as any
								)?.phoneNumber
							)
						}
						helperText={
							(formik.touched[`${selectedStatus}Data`] as any)?.phoneNumber &&
							(
								formik.errors[
									`${selectedStatus}Data` as keyof typeof formik.errors
								] as any
							)?.phoneNumber
						}
					/>
				</Grid2>
			</Grid2>

			{/* Website/Social Networks Section - Only for structure */}
			{selectedStatus === "structure" && (
				<>
					<Typography variant="h6" sx={{ mb: 2, fontWeight: 500, mt: 4 }}>
						{t("freelancerOnboardingMyProfessionalInformation.websiteSocial.title")}
					</Typography>
					<Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
						{t("freelancerOnboardingMyProfessionalInformation.websiteSocial.description")}
					</Typography>
					{/* Toggle button for Status URLs */}
					<Grid2 container spacing={2} sx={{ mb: 2 }}>
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Button
								startIcon={<LinkIcon />}
								onClick={() => setActiveCompanyUrlTable(!activeCompanyUrlTable)}
								variant="outlined"
								size="large"
								disableRipple
								fullWidth
								sx={{
									color: zoullooColors.grey[700],
									borderColor: activeCompanyUrlTable
										? zoullooColors.secondary?.main
										: zoullooColors.grey[400],
									borderWidth: activeCompanyUrlTable ? 2 : 1,
									"&:hover": {
										borderColor: activeCompanyUrlTable
											? zoullooColors.secondary?.main
											: zoullooColors.grey[700],
										backgroundColor: zoullooColors.grey[100]
									}
								}}
							>
								{t("freelancerOnboardingMyProfessionalInformation.websiteSocial.addUrl")}
							</Button>
						</Grid2>
					</Grid2>
					{/* Status URLs Table - only show when toggle is active */}
					<UrlTable
						urls={getCurrentStatusData(selectedStatus)?.companyUrls || []}
						isVisible={activeCompanyUrlTable}
						isAddingUrl={isAddingCompanyUrl}
						addingUrl={addingCompanyUrl}
						onAddNewUrl={handleAddNewCompanyUrl}
						onConfirmAddUrl={handleConfirmAddCompanyUrl}
						onCancelAddUrl={handleCancelAddCompanyUrl}
						onRemoveUrl={removeCompanyUrl}
						onUrlChange={(url: any) =>
							setAddingCompanyUrl(prev => ({ ...prev!, ...url }))
						}
						showTypeColumn={true}
						urlTypes={urlTypes}
						tableWidth="80%"
					/>
				</>
			)}

			{/* Certification Section — not required when registration is in progress */}
			<Box sx={{ mt: 4, mb: 3, display: isRegistrationInProgress ? "none" : "block" }}>
				<FormControlLabel
					sx={{
						color:
							(formik.touched[`${selectedStatus}Data`] as any)
								?.areCompanyInformationAcknowledged &&
							getCurrentStatusData(selectedStatus)?.areCompanyInformationAcknowledged !==
								true
								? "red"
								: ""
					}}
					control={
						<Checkbox
							color="secondary"
							checked={
								getCurrentStatusData(selectedStatus)?.areCompanyInformationAcknowledged ||
								false
							}
							onChange={e => {
								updateStatusData(selectedStatus, {
									areCompanyInformationAcknowledged: e.target.checked
								});
								formik.setFieldTouched(
									`${selectedStatus}Data.areCompanyInformationAcknowledged`,
									true
								);
								formik.setFieldValue(
									`${selectedStatus}Data.areCompanyInformationAcknowledged`,
									e.target.checked
								);
							}}
							onBlur={() => {
								formik.setFieldTouched(
									`${selectedStatus}Data.areCompanyInformationAcknowledged`,
									true
								);
							}}
							name={`${selectedStatus}Data.areCompanyInformationAcknowledged`}
						/>
					}
					label={t("freelancerOnboardingMyProfessionalInformation.certification.label")}
				/>
			</Box>
		</Box>
	);
};

export default CompanyInfoSection;
